import './style.css'
import * as THREE from 'three'
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js'
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js'
import * as dat from 'lil-gui'
// import gradientVertexShader from './shaders/gradientVertex.glsl'
// import gradientFragmentShader from './shaders/gradientFragment.glsl'
// import particlesVertexShader from './shaders/particlesVertex.glsl'
// import particlesFragmentShader from './shaders/particlesFragment.glsl'
// import vignetteVertexShader from './shaders/vignetteVertex.glsl'
// import vignetteFragmentShader from './shaders/vignetteFragment.glsl'
// Original background idea by Bruno Simon

/**
 * Base
 */
// Debug
const gui = new dat.GUI()

gui.hide()
window.addEventListener('keydown', (event) => {
  if (event.key === 'd') {
    if (gui._hidden) gui.show()
    else gui.hide()
  }
})

// Canvas
const canvas = document.querySelector('.webgl')

// Scene
const scene = new THREE.Scene()

/**
 * Textures
 */
const textureLoader = new THREE.TextureLoader()
const particleTexture = textureLoader.load('https://static.igem.wiki/teams/4432/wiki/animation-wiki/particlemask.png')
const smokeTexture = textureLoader.load('https://static.igem.wiki/teams/4432/wiki/animation-wiki/smoke.png')


/**
 * Gradient
 */
// Geometry
const gradientGeometry = new THREE.PlaneGeometry(2, 2, 1, 1)

// Gradient Material
let colors = {}

colors.top = {}
colors.top.value = '#1a2036'
colors.top.instance = new THREE.Color(colors.top.value)

colors.bottom = {}
colors.bottom.saturation = 32
colors.bottom.lightness = 38
colors.bottom.value = `hsl(200, ${colors.bottom.saturation}%, ${colors.bottom.lightness}%)`
colors.bottom.instance = new THREE.Color(colors.bottom.value)

const gradientMaterial = new THREE.ShaderMaterial({
  depthWrite: false,
  depthTest: true,
  // transparent: true,
  uniforms:
  {
    uTime: { value: 0 },
    uTopColor: { value: colors.top.instance },
    uBottomColor: { value: colors.bottom.instance },
  },
  vertexShader: `
  varying vec2 vUv;

  void main()
  {
      gl_Position = vec4(position, 1.0);

      vUv = uv;
  }`,
  fragmentShader: `
  uniform float uTime;
  uniform vec3 uBottomColor;
  uniform vec3 uTopColor;


  varying vec2 vUv;

  vec3 hslToRgb(vec3 c)
  {
    vec4 K = vec4(1.0, 2.0 / 3.0, 1.0 / 3.0, 3.0);
    vec3 p = abs(fract(c.xxx + K.xyz) * 6.0 - K.www);
    return c.z * mix(K.xxx, clamp(p - K.xxx, 0.0, 1.0), c.y);
  }

  float random2d(vec2 n) { 
    return fract(sin(dot(n, vec2(12.9898, 78.233))) * 43758.5453123);
  }

  void main()
  {

    vec3 finalColor = mix(uBottomColor, uTopColor, vUv.y);

    finalColor += random2d(vUv) * 0.01; // noise

    gl_FragColor = vec4(finalColor, 1.0);
  }`
})

// Debug
gui
  .addColor(
    colors.top,
    'value',
    {
      view: 'color'
    }
  )
  .onChange(() => {
    colors.top.instance.set(colors.top.value)
  })
  .name('Gradient Top Color')

gui
  .add(colors.bottom, 'saturation',)
  .min(0.0)
  .max(100.0)
  .name('Gradient Saturation')

gui
  .add(colors.bottom, 'lightness',)
  .min(0.0)
  .max(100.0)
  .name('Gradient Lightness')

// Gradient Mesh
const gradientMesh = new THREE.Mesh(gradientGeometry, gradientMaterial)
scene.add(gradientMesh)

/**
 * Smoke
 */
// Geometry
const smokeGeometry = new THREE.PlaneGeometry(1, 1, 1, 1)

// Smoke Mesh
let smokeSettings = {
  smokeCount: 30,
  smokeMinSize: 3,
  smokeSizeMultiplier: 3,
  smokeRotationSpeedMultiplier: 1,
  smokeFloatingSpeedMultiplier: 0.6,
  smokeLerp: 0.3,
}

gui
  .add(smokeSettings, 'smokeLerp')
  .min(0)
  .max(1)
  .name('Smoke Lerp')

let smokeGroup = new THREE.Group()
smokeGroup.position.y = -2
scene.add(smokeGroup)

let items = []
// let maxScale = 3
// let minScale = 0.6

let smokeColor = {}

smokeColor.value = '#ffffff'
smokeColor.instance = new THREE.Color(smokeColor.value)

for (let i = 0; i < smokeSettings.smokeCount; i++) {
  const item = {}

  // Smoke Material
  const smokeMaterial = new THREE.MeshBasicMaterial({
    // wireframe: true,
    depthWrite: false,
    transparent: true,
    blending: THREE.AdditiveBlending,
    alphaMap: smokeTexture,
    opacity: 0.01 + Math.random() * 0.2
  })

  smokeMaterial.color = smokeColor.instance

  // Mesh
  item.mesh = new THREE.Mesh(smokeGeometry, smokeMaterial)

  const randomPosX = (Math.random() - 0.5) * 14
  if (randomPosX < 3.5 && randomPosX > -3.5) {
    i--
    continue
  } else {
    item.mesh.position.x = randomPosX
  }

  // const randomScale = (Math.random() * (maxScale - minScale)) + minScale;
  const randomScale = smokeSettings.smokeMinSize + Math.random() * smokeSettings.smokeSizeMultiplier
  item.mesh.scale.set(randomScale, randomScale)

  item.smokeRotationSpeed = (Math.random() - 0.5) * Math.random() * smokeSettings.smokeRotationSpeedMultiplier
  item.smokeFloatingSpeed = Math.random() * smokeSettings.smokeFloatingSpeedMultiplier

  smokeGroup.add(item.mesh)

  // Save
  items.push(item)
}

/**
 * Particles
 */
// Particles Geometry & Random placement
let particlesSettings = {
  count: 5000,
}

let particlesGeometry = new THREE.BufferGeometry()

const positionArray = new Float32Array(particlesSettings.count * 3)
const progressArray = new Float32Array(particlesSettings.count)
const sizeArray = new Float32Array(particlesSettings.count)
const alphaArray = new Float32Array(particlesSettings.count)

for (let i = 0; i < particlesSettings.count; i++) {
  positionArray[i * 3 + 0] = (Math.random() - 0.5) * 14
  positionArray[i * 3 + 1] = 0
  positionArray[i * 3 + 2] = (Math.random() - 0.5) * 10

  progressArray[i] = Math.random()
  sizeArray[i] = Math.random()
  alphaArray[i] = Math.random()

}
particlesGeometry.setAttribute('position', new THREE.Float32BufferAttribute(positionArray, 3))
particlesGeometry.setAttribute('aProgress', new THREE.Float32BufferAttribute(progressArray, 1))
particlesGeometry.setAttribute('aSize', new THREE.Float32BufferAttribute(sizeArray, 1))
particlesGeometry.setAttribute('aAlpha', new THREE.Float32BufferAttribute(alphaArray, 1))

// Particles Material
const particlesMaterial = new THREE.ShaderMaterial({
  transparent: true,
  // depthTest: false,
  depthWrite: false,
  blending: THREE.AdditiveBlending,
  uniforms:
  {
    uSize: { value: 30 },
    uParticlesSpeed: { value: 0.01 },
    uPerlinFrequency: { value: 0.5 },
    uPerlinMultiplier: { value: 3.0 },
    uMask: { value: particleTexture },
    uTime: { value: 0 },
  },
  vertexShader: `
  uniform float uTime;
  uniform float uSize;
  uniform float uParticlesSpeed;
  uniform float uPerlinFrequency;
  uniform float uPerlinMultiplier;
  
  attribute float aProgress;
  attribute float aSize;
  attribute float aAlpha;
  
  varying float vAlpha;
  
  //	Classic Perlin 3D Noise 
  //	by Stefan Gustavson
  //
  vec4 permute(vec4 x){return mod(((x*34.0)+1.0)*x, 289.0);}
  vec4 taylorInvSqrt(vec4 r){return 1.79284291400159 - 0.85373472095314 * r;}
  vec3 fade(vec3 t) {return t*t*t*(t*(t*6.0-15.0)+10.0);}
  
  float perlin3d(vec3 P){
    vec3 Pi0 = floor(P); 
    vec3 Pi1 = Pi0 + vec3(1.0); 
    Pi0 = mod(Pi0, 289.0);
    Pi1 = mod(Pi1, 289.0);
    vec3 Pf0 = fract(P); 
    vec3 Pf1 = Pf0 - vec3(1.0); 
    vec4 ix = vec4(Pi0.x, Pi1.x, Pi0.x, Pi1.x);
    vec4 iy = vec4(Pi0.yy, Pi1.yy);
    vec4 iz0 = Pi0.zzzz;
    vec4 iz1 = Pi1.zzzz;
  
    vec4 ixy = permute(permute(ix) + iy);
    vec4 ixy0 = permute(ixy + iz0);
    vec4 ixy1 = permute(ixy + iz1);
  
    vec4 gx0 = ixy0 / 7.0;
    vec4 gy0 = fract(floor(gx0) / 7.0) - 0.5;
    gx0 = fract(gx0);
    vec4 gz0 = vec4(0.5) - abs(gx0) - abs(gy0);
    vec4 sz0 = step(gz0, vec4(0.0));
    gx0 -= sz0 * (step(0.0, gx0) - 0.5);
    gy0 -= sz0 * (step(0.0, gy0) - 0.5);
  
    vec4 gx1 = ixy1 / 7.0;
    vec4 gy1 = fract(floor(gx1) / 7.0) - 0.5;
    gx1 = fract(gx1);
    vec4 gz1 = vec4(0.5) - abs(gx1) - abs(gy1);
    vec4 sz1 = step(gz1, vec4(0.0));
    gx1 -= sz1 * (step(0.0, gx1) - 0.5);
    gy1 -= sz1 * (step(0.0, gy1) - 0.5);
  
    vec3 g000 = vec3(gx0.x,gy0.x,gz0.x);
    vec3 g100 = vec3(gx0.y,gy0.y,gz0.y);
    vec3 g010 = vec3(gx0.z,gy0.z,gz0.z);
    vec3 g110 = vec3(gx0.w,gy0.w,gz0.w);
    vec3 g001 = vec3(gx1.x,gy1.x,gz1.x);
    vec3 g101 = vec3(gx1.y,gy1.y,gz1.y);
    vec3 g011 = vec3(gx1.z,gy1.z,gz1.z);
    vec3 g111 = vec3(gx1.w,gy1.w,gz1.w);
  
    vec4 norm0 = taylorInvSqrt(vec4(dot(g000, g000), dot(g010, g010), dot(g100, g100), dot(g110, g110)));
    g000 *= norm0.x;
    g010 *= norm0.y;
    g100 *= norm0.z;
    g110 *= norm0.w;
    vec4 norm1 = taylorInvSqrt(vec4(dot(g001, g001), dot(g011, g011), dot(g101, g101), dot(g111, g111)));
    g001 *= norm1.x;
    g011 *= norm1.y;
    g101 *= norm1.z;
    g111 *= norm1.w;
  
    float n000 = dot(g000, Pf0);
    float n100 = dot(g100, vec3(Pf1.x, Pf0.yz));
    float n010 = dot(g010, vec3(Pf0.x, Pf1.y, Pf0.z));
    float n110 = dot(g110, vec3(Pf1.xy, Pf0.z));
    float n001 = dot(g001, vec3(Pf0.xy, Pf1.z));
    float n101 = dot(g101, vec3(Pf1.x, Pf0.y, Pf1.z));
    float n011 = dot(g011, vec3(Pf0.x, Pf1.yz));
    float n111 = dot(g111, Pf1);
  
    vec3 fade_xyz = fade(Pf0);
    vec4 n_z = mix(vec4(n000, n100, n010, n110), vec4(n001, n101, n011, n111), fade_xyz.z);
    vec2 n_yz = mix(n_z.xy, n_z.zw, fade_xyz.y);
    float n_xyz = mix(n_yz.x, n_yz.y, fade_xyz.x); 
    return 2.2 * n_xyz;
  }
  
  void main()
  {
    float progress = mod(aProgress + uTime * uParticlesSpeed, 1.0); // particle vertical speed
  
    vec4 modelPosition = modelMatrix * vec4(position, 1.0);
    modelPosition.y += progress * 10.0;
    modelPosition.x += perlin3d(modelPosition.xyz * uPerlinFrequency) * uPerlinMultiplier;
  
    vec4 viewPosition = viewMatrix * modelPosition;
    gl_Position = projectionMatrix * viewPosition;
    
    gl_PointSize = uSize * aSize; // particles size
    gl_PointSize *= (1.0 / - viewPosition.z);
  
    vAlpha = aAlpha;
  }`,
  fragmentShader: `
  uniform sampler2D uMask;

  varying float vAlpha; 

  void main()
  {
    float maskStrength = texture2D(uMask, gl_PointCoord).r;
    gl_FragColor = vec4(1.0, 1.0, 1.0, maskStrength * vAlpha);
  }`
})

// Particles Debug
gui
  .add(particlesSettings, 'count')
  .min(0)
  .max(10000)
  .step(10)
  .name('Particles Count')
  .onChange(value => {
    particlesSettings.count = value
    particles.updateMatrix();
  })

gui
  .add(particlesMaterial.uniforms.uSize, 'value',)
  .min(1.0)
  .max(100.0)
  .step(0.01)
  .name('Particles size')

gui
  .add(particlesMaterial.uniforms.uParticlesSpeed, 'value',)
  .min(0.0)
  .max(0.1)
  .step(0.0001)
  .name('Particles speed')

gui
  .add(particlesMaterial.uniforms.uPerlinFrequency, 'value',)
  .min(0.0)
  .max(3)
  .step(0.01)
  .name('Perlin Frequency')

gui
  .add(particlesMaterial.uniforms.uPerlinMultiplier, 'value',)
  .min(0.0)
  .max(10)
  .step(0.1)
  .name('Perlin multiplier')


// Particles Mesh
const particles = new THREE.Points(particlesGeometry, particlesMaterial)
particles.position.y = -5
scene.add(particles)

/**
 * Vignette
 */
// Geometry
const vignetteGeometry = new THREE.PlaneGeometry(2, 2, 1, 1)

// Material
let vignetteColor = {}

vignetteColor.value = '#130819'
vignetteColor.instance = new THREE.Color(vignetteColor.value)


const vignetteMaterial = new THREE.ShaderMaterial({
  // wireframe: true,
  depthWrite: false,
  depthTest: false,
  transparent: true,
  uniforms:
  {
    uVignetteColor: { value: vignetteColor.instance },
    uOffset: { value: 0.0 },
    uMultiplier: { value: 1.2 }
  },
  vertexShader: `
  varying vec2 vUv;

  void main()
  {
      gl_Position = vec4(position, 1.0);

      vUv = uv;
  }`,
  fragmentShader: `
  uniform vec3 uVignetteColor;
  uniform float uOffset;
  uniform float uMultiplier;
  
  varying vec2 vUv;
  
  void main()
  {
    float alpha = length(vUv - 0.5);
    alpha += uOffset;
    alpha *= uMultiplier;
  
    gl_FragColor = vec4(uVignetteColor, alpha);
  }`
})

// vignette Mesh
const vignetteMesh = new THREE.Mesh(vignetteGeometry, vignetteMaterial)
scene.add(vignetteMesh)

// Debug
gui
  .addColor(
    vignetteColor,
    'value',
    {
      view: 'color'
    }
  )
  .onChange(() => {
    vignetteColor.instance.set(vignetteColor.value)
  })
  .name('Vignette Color')

gui
  .add(vignetteMaterial.uniforms.uOffset, 'value',)
  .min(-1)
  .max(1)
  .step(0.01)
  .name('Vignette Offset')

gui
  .add(vignetteMaterial.uniforms.uMultiplier, 'value',)
  .min(0)
  .max(2)
  .step(0.01)
  .name('Vignette Multiplier')

/**
 * 3D Models
 */
const gltfLoader = new GLTFLoader()
// console.log(gltfLoader);
gltfLoader.load(
  'https://static.igem.wiki/teams/4432/wiki/animation-wiki/dna2.gltf',
  (gltf) => {
    gltf.scene.scale.set(0.10, 0.10, 0.10)
    gltf.scene.position.x = -3
    gltf.scene.position.y = -1
    gltf.scene.position.z = 0
    // gltf.scene.rotation.x = -0.6
    // gltf.scene.rotation.z = 0.4
    scene.add(gltf.scene)
  }
)

gltfLoader.load(
  'https://static.igem.wiki/teams/4432/wiki/animation-wiki/dna2.gltf',
  (gltf) => {
    gltf.scene.scale.set(0.10, 0.10, 0.10)
    gltf.scene.position.y = -1
    gltf.scene.position.x = 3.5
    gltf.scene.position.z = 0
    gltf.scene.rotation.y = Math.PI / 2
    // gltf.scene.rotation.z = Math.PI
    // gltf.scene.rotation.y = Math.PI / 2
    scene.add(gltf.scene)
  }
)

const ambientLight = new THREE.AmbientLight(0xffffff, 0.8)
scene.add(ambientLight)

const pointLight = new THREE.PointLight(0xafbce1, 20)
pointLight.position.z = -8
scene.add(pointLight)

/**
 * Sizes
 */
const sizes = {
  width: window.innerWidth,
  height: window.innerHeight
}

window.addEventListener('resize', () => {
  // Update sizes
  sizes.width = window.innerWidth
  sizes.height = window.innerHeight

  // Update camera
  camera.aspect = sizes.width / sizes.height
  camera.updateProjectionMatrix()

  // Update renderer
  renderer.setSize(sizes.width, sizes.height)
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))
})

// Helper
// const axesHelper = new THREE.AxesHelper();
// scene.add(axesHelper);

/**
 * Camera
 */
// Base camera
const camera = new THREE.PerspectiveCamera(55, sizes.width / sizes.height, 0.1, 100)
scene.add(camera)
camera.position.z = 5

// Controls
// const controls = new OrbitControls(camera, canvas)
// controls.enableDamping = true
// controls.enablePan = true
// controls.enableZoom = true

// Cursor
//  const cursor = {
//   x: 0,
//   y: 0
// }

// window.addEventListener('mousemove', (event) => {
//   cursor.x = event.clientX / sizes.width - 0.5
//   cursor.y = -(event.clientY / sizes.height - 0.5)

//   console.log(cursor);
// })

/**
 * Renderer
 */
const renderer = new THREE.WebGLRenderer({
  canvas: canvas
})
renderer.setSize(sizes.width, sizes.height)
renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))

/**
 * Animate
 */
const clock = new THREE.Clock()
let previousTime = 0

const tick = () => {
  let elapsedTime = clock.getElapsedTime()
  const deltaTime = elapsedTime - previousTime
  previousTime = elapsedTime

  // elapsedTime *= 20
  elapsedTime += 61

  // Update Particles shader 
  particlesMaterial.uniforms.uTime.value = elapsedTime

  // Update Gradient shader 
  gradientMaterial.uniforms.uTime.value = elapsedTime
  colors.bottom.value = `hsl(${Math.round(elapsedTime * 2)}, ${Math.round(colors.bottom.saturation)}%, ${Math.round(colors.bottom.lightness)}%)`
  colors.bottom.instance.set(colors.bottom.value)

  // Update smoke 
  //// Mesh Rotation
  for (const item of items) {
    item.mesh.rotation.z = elapsedTime * item.smokeRotationSpeed
    item.mesh.position.y = Math.sin(elapsedTime * item.smokeFloatingSpeed)
  }

  //// Color
  smokeColor.instance.set(colors.bottom.instance.clone())
  smokeColor.instance.lerp(new THREE.Color('#ffffff'), smokeSettings.smokeLerp)

  // Update controls
  // controls.update()
  // camera.position.x = cursor.x * 0.1
  // camera.position.y = cursor.y * 0.1

  // Render
  renderer.render(scene, camera)

  // Call tick again on the next frame
  window.requestAnimationFrame(tick)
}

tick()